import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Loader2, Upload, Download, Plus, Trash2, Edit2, X,
  FileSpreadsheet, BookOpen, Shield, AlertCircle, Check, Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import MCQAgentDrafts from '../components/MCQAgentDrafts';
import { toast } from 'sonner';

const QUESTION_TYPES = [
  { id: 'grammar_cloze', name: 'Grammar Cloze' },
  { id: 'vocabulary_cloze', name: 'Vocabulary Cloze' },
  { id: 'grammar_mcq', name: 'Grammar MCQ' },
  { id: 'vocabulary_mcq', name: 'Vocabulary MCQ' },
  { id: 'synthesis', name: 'Synthesis & Transformation' },
  { id: 'editing', name: 'Editing' },
  { id: 'comprehension_cloze', name: 'Comprehension Cloze' },
  { id: 'antonyms_synonyms', name: 'Antonyms & Synonyms' },
  { id: 'regular_irregular_verbs', name: 'Regular & Irregular Verbs' }
];

const MCQ_TYPES = ['grammar_mcq', 'vocabulary_mcq', 'antonyms_synonyms', 'regular_irregular_verbs'];

// Question types that require a sub-topic selection with independent progress tracking
const SUBTOPIC_MAP = {
  antonyms_synonyms: ['synonyms', 'antonyms'],
  regular_irregular_verbs: ['regular', 'irregular']
};

const SUBTOPIC_LABELS = {
  synonyms: 'Synonyms',
  antonyms: 'Antonyms',
  regular: 'Regular',
  irregular: 'Irregular'
};

const emptyForm = {
  question_type: 'grammar_mcq',
  level: 'P5',
  topic: '',
  question_text: '',
  options: ['', '', '', ''],
  correct_answer: '',
  explanation: '',
  subtopic: ''
};

const QuestionManagerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [typeFilter, setTypeFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [tab, setTab] = useState('list'); // list | add | upload

  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, levelFilter]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const data = await api.listCustomQuestions(
        typeFilter === 'all' ? null : typeFilter,
        levelFilter === 'all' ? null : levelFilter
      );
      setQuestions(data.questions || []);
      setTotal(data.total || 0);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Error fetching questions:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load questions');
      }
    } finally {
      setLoading(false);
    }
  };

  const isMcq = MCQ_TYPES.includes(formData.question_type);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const startEdit = (q) => {
    setFormData({
      question_type: q.question_type,
      level: q.level,
      topic: q.topic,
      question_text: q.question_text,
      options: q.options && q.options.length > 0 ? [...q.options, '', '', '', ''].slice(0, Math.max(4, q.options.length)) : ['', '', '', ''],
      correct_answer: q.correct_answer,
      explanation: q.explanation || '',
      subtopic: q.subtopic || ''
    });
    setEditingId(q.id);
    setTab('add');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      question_type: formData.question_type,
      level: formData.level,
      topic: formData.topic.trim(),
      question_text: formData.question_text.trim(),
      correct_answer: formData.correct_answer.trim(),
      explanation: formData.explanation.trim()
    };

    if (SUBTOPIC_MAP[formData.question_type]) {
      const sub = (formData.subtopic || '').trim().toLowerCase();
      const allowed = SUBTOPIC_MAP[formData.question_type];
      if (!allowed.includes(sub)) {
        const nice = allowed.map(a => SUBTOPIC_LABELS[a] || a).join(' or ');
        toast.error(`This question type requires a sub-topic (${nice})`);
        setSaving(false);
        return;
      }
      payload.subtopic = sub;
    } else {
      payload.subtopic = null;
    }

    if (isMcq) {
      const opts = formData.options.map(o => o.trim()).filter(Boolean);
      if (opts.length < 2) {
        toast.error('MCQ questions need at least 2 options');
        setSaving(false);
        return;
      }
      payload.options = opts;
      if (!opts.includes(payload.correct_answer)) {
        toast.error(`Correct answer "${payload.correct_answer}" must match one of the options`);
        setSaving(false);
        return;
      }
    } else {
      payload.options = null;
    }

    try {
      if (editingId) {
        await api.updateCustomQuestion(editingId, payload);
        toast.success('Question updated');
      } else {
        await api.createCustomQuestion(payload);
        toast.success('Question created');
      }
      resetForm();
      setTab('list');
      fetchQuestions();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question? This cannot be undone.')) return;
    try {
      await api.deleteCustomQuestion(id);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const data = await api.downloadCsvTemplate();
      const blob = new Blob([data.content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', data.filename || 'template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Template error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const result = await api.bulkUploadQuestions(file);
      setUploadResult(result);
      if (result.inserted > 0) {
        toast.success(`Uploaded ${result.inserted} questions`);
        fetchQuestions();
      }
      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} row(s) had errors — see details below`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F8FAFC]">
        <Card className="p-8 rounded-3xl max-w-md text-center border-2 border-red-200" data-testid="questions-access-denied">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-red-700 mb-2">Access Denied</h1>
          <p className="text-slate-600 mb-6">Only admins and teachers can manage custom questions.</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-8">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/admin')} variant="outline" size="sm" className="rounded-full" data-testid="qm-back-button">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-heading font-bold text-emerald-600" data-testid="qm-title">Question Bank</h1>
                <p className="text-sm text-slate-500">Manage curated practice questions</p>
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-12 h-12 object-contain"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'list', label: 'All Questions', icon: BookOpen, testid: 'qm-tab-list' },
            { id: 'add', label: editingId ? 'Edit Question' : 'Add Question', icon: Plus, testid: 'qm-tab-add' },
            { id: 'upload', label: 'Bulk Upload (CSV)', icon: Upload, testid: 'qm-tab-upload' },
            { id: 'drafts', label: 'AI Drafts', icon: Sparkles, testid: 'qm-tab-drafts' }
          ].map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => {
                  setTab(t.id);
                  if (t.id !== 'add') resetForm();
                }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition ${
                  tab === t.id
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-emerald-300'
                }`}
                data-testid={t.testid}
              >
                <Icon className="w-4 h-4" strokeWidth={2.5} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* LIST TAB */}
        {tab === 'list' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Filters */}
            <Card className="p-4 rounded-2xl border-2 border-slate-200 mb-6">
              <div className="flex flex-col md:flex-row gap-3">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="rounded-full md:w-64" data-testid="qm-filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {QUESTION_TYPES.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="rounded-full md:w-48" data-testid="qm-filter-level">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="P4">Primary 4</SelectItem>
                    <SelectItem value="P5">Primary 5</SelectItem>
                    <SelectItem value="P6">Primary 6</SelectItem>
                  </SelectContent>
                </Select>
                <div className="ml-auto text-sm text-slate-600 self-center font-semibold" data-testid="qm-total-count">
                  {total} question{total !== 1 ? 's' : ''}
                </div>
              </div>
            </Card>

            {/* Questions List */}
            {loading ? (
              <div className="text-center py-16" data-testid="qm-loading">
                <Loader2 className="w-16 h-16 animate-spin text-emerald-500 mx-auto" />
              </div>
            ) : questions.length === 0 ? (
              <Card className="p-16 rounded-3xl border-2 border-dashed border-slate-300 text-center" data-testid="qm-empty">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-bold text-slate-800 mb-2">No questions yet</h3>
                <p className="text-slate-600 mb-6">Add your first question or bulk upload a CSV.</p>
                <div className="flex justify-center gap-3">
                  <Button onClick={() => setTab('add')} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Add Question
                  </Button>
                  <Button onClick={() => setTab('upload')} variant="outline" className="rounded-full">
                    <Upload className="w-4 h-4 mr-2" /> Upload CSV
                  </Button>
                </div>
              </Card>
            ) : (
              <div className="space-y-3">
                {questions.map((q, idx) => (
                  <Card key={q.id} className="p-4 rounded-2xl border-2 border-slate-200 hover:border-emerald-200 transition" data-testid={`qm-question-row-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold">
                            {QUESTION_TYPES.find(t => t.id === q.question_type)?.name || q.question_type}
                          </span>
                          <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full text-xs font-bold">
                            {q.level}
                          </span>
                          <span className="text-xs text-slate-600 font-semibold">· {q.topic}</span>
                          {q.subtopic && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              q.subtopic === 'synonyms' || q.subtopic === 'regular'
                                ? 'bg-teal-100 text-teal-700'
                                : 'bg-indigo-100 text-indigo-700'
                            }`}>
                              {SUBTOPIC_LABELS[q.subtopic] || q.subtopic}
                            </span>
                          )}
                          {q.source === 'csv' && (
                            <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full text-xs font-semibold">CSV</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-800 mb-2 line-clamp-2" data-testid={`qm-question-text-${idx}`}>
                          {q.question_text}
                        </p>
                        {q.options && (
                          <p className="text-xs text-slate-500 mb-1">
                            Options: {q.options.join(' · ')}
                          </p>
                        )}
                        <p className="text-xs text-emerald-700 font-semibold">
                          ✓ Answer: {q.correct_answer}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          onClick={() => startEdit(q)}
                          variant="outline"
                          size="sm"
                          className="rounded-full h-8 w-8 p-0"
                          data-testid={`qm-edit-${idx}`}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(q.id)}
                          variant="outline"
                          size="sm"
                          className="rounded-full h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`qm-delete-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ADD TAB */}
        {tab === 'add' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="p-8 rounded-3xl border-2 border-slate-200 max-w-3xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold text-slate-800">
                  {editingId ? 'Edit Question' : 'Add New Question'}
                </h2>
                {editingId && (
                  <Button onClick={() => { resetForm(); setTab('list'); }} variant="outline" size="sm" className="rounded-full">
                    <X className="w-4 h-4 mr-1" /> Cancel Edit
                  </Button>
                )}
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Question Type</Label>
                    <Select value={formData.question_type} onValueChange={(v) => setFormData({ ...formData, question_type: v })}>
                      <SelectTrigger className="mt-1 rounded-2xl border-2" data-testid="qm-form-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUESTION_TYPES.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Level</Label>
                    <Select value={formData.level} onValueChange={(v) => setFormData({ ...formData, level: v })}>
                      <SelectTrigger className="mt-1 rounded-2xl border-2" data-testid="qm-form-level">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="P4">Primary 4</SelectItem>
                        <SelectItem value="P5">Primary 5</SelectItem>
                        <SelectItem value="P6">Primary 6</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Topic</Label>
                  <Input
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    className="mt-1 rounded-2xl border-2"
                    placeholder="e.g., Subject-Verb Agreement, Present Tense, Synonyms"
                    required
                    data-testid="qm-form-topic"
                  />
                </div>

                {SUBTOPIC_MAP[formData.question_type] && (
                  <div>
                    <Label>Sub-topic <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.subtopic || ''}
                      onValueChange={(v) => setFormData({ ...formData, subtopic: v })}
                    >
                      <SelectTrigger className="mt-1 rounded-2xl border-2" data-testid="qm-form-subtopic">
                        <SelectValue placeholder={`Choose ${SUBTOPIC_MAP[formData.question_type].map(k => SUBTOPIC_LABELS[k] || k).join(' or ').toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {SUBTOPIC_MAP[formData.question_type].map(k => (
                          <SelectItem key={k} value={k}>{SUBTOPIC_LABELS[k] || k}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Progress is tracked separately for each sub-topic.</p>
                  </div>
                )}

                <div>
                  <Label>Question Text</Label>
                  <Textarea
                    value={formData.question_text}
                    onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                    className="mt-1 rounded-2xl border-2 min-h-24"
                    placeholder="Type the full question. Use underscores (_____) for blanks in cloze passages."
                    required
                    data-testid="qm-form-question"
                  />
                </div>

                {isMcq && (
                  <div>
                    <Label>MCQ Options (2–4)</Label>
                    <div className="space-y-2 mt-1">
                      {formData.options.map((opt, i) => (
                        <div key={i} className="flex gap-2">
                          <span className="w-8 h-11 flex items-center justify-center text-sm text-slate-500 font-bold">
                            {String.fromCharCode(65 + i)}.
                          </span>
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const newOpts = [...formData.options];
                              newOpts[i] = e.target.value;
                              setFormData({ ...formData, options: newOpts });
                            }}
                            className="rounded-2xl border-2 flex-1"
                            placeholder={`Option ${String.fromCharCode(65 + i)}`}
                            data-testid={`qm-form-option-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label>Correct Answer</Label>
                  <Input
                    value={formData.correct_answer}
                    onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                    className="mt-1 rounded-2xl border-2"
                    placeholder={isMcq ? 'Must match one of the options exactly' : 'The correct answer text'}
                    required
                    data-testid="qm-form-answer"
                  />
                </div>

                <div>
                  <Label>Explanation (optional)</Label>
                  <Textarea
                    value={formData.explanation}
                    onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                    className="mt-1 rounded-2xl border-2 min-h-20"
                    placeholder="Explain why this is the correct answer — shown to students after they submit."
                    data-testid="qm-form-explanation"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-base font-bold tactile-button border-emerald-700"
                    data-testid="qm-form-submit"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" strokeWidth={3} />}
                    {editingId ? 'Save Changes' : 'Add Question'}
                  </Button>
                  <Button
                    type="button"
                    onClick={resetForm}
                    variant="outline"
                    className="rounded-full py-6 px-6"
                    data-testid="qm-form-reset"
                  >
                    Reset
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        )}

        {/* UPLOAD TAB */}
        {tab === 'upload' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="p-8 rounded-3xl border-2 border-slate-200 max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-emerald-100 p-3 rounded-2xl">
                  <FileSpreadsheet className="w-6 h-6 text-emerald-600" strokeWidth={2.5} />
                </div>
                <div>
                  <h2 className="text-2xl font-heading font-bold text-slate-800">Bulk Upload CSV</h2>
                  <p className="text-sm text-slate-600">Add many questions at once</p>
                </div>
              </div>

              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4 mb-6">
                <h3 className="text-sm font-bold text-emerald-800 mb-2">📋 CSV Format</h3>
                <ul className="text-xs text-slate-700 space-y-1 mb-3">
                  <li>• Columns: <code className="bg-white px-1.5 rounded">question_type, level, topic, question_text, options, correct_answer, explanation, subtopic</code></li>
                  <li>• <strong>options</strong>: for MCQ types only, pipe-separated (e.g. <code className="bg-white px-1.5 rounded">is|are|were|have</code>)</li>
                  <li>• <strong>question_type</strong>: one of <code className="bg-white px-1.5 rounded">grammar_cloze, vocabulary_cloze, grammar_mcq, vocabulary_mcq, synthesis, editing, comprehension_cloze, antonyms_synonyms, regular_irregular_verbs</code></li>
                  <li>• <strong>subtopic</strong>: required for <code className="bg-white px-1.5 rounded">antonyms_synonyms</code> (<code>synonyms</code>|<code>antonyms</code>) and <code className="bg-white px-1.5 rounded">regular_irregular_verbs</code> (<code>regular</code>|<code>irregular</code>). Leave blank for other types.</li>
                  <li>• <strong>level</strong>: P4, P5, or P6</li>
                </ul>
                <Button
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  size="sm"
                  className="rounded-full border-emerald-400 text-emerald-700"
                  data-testid="qm-download-template"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV Template
                </Button>
              </div>

              <div className="border-2 border-dashed border-emerald-300 rounded-2xl p-8 text-center bg-emerald-50/30">
                <Upload className="w-12 h-12 text-emerald-500 mx-auto mb-3" strokeWidth={2.5} />
                <p className="text-slate-700 font-semibold mb-2">Select your CSV file</p>
                <p className="text-xs text-slate-500 mb-4">Max 5MB · UTF-8 encoded</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                  id="csv-file-input"
                  data-testid="qm-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6"
                  data-testid="qm-upload-button"
                >
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : 'Choose File'}
                </Button>
              </div>

              {uploadResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6"
                  data-testid="qm-upload-result"
                >
                  {uploadResult.inserted > 0 && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 mb-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Check className="w-5 h-5 text-green-600" strokeWidth={3} />
                        <p className="font-bold text-green-700">Successfully uploaded {uploadResult.inserted} question(s)</p>
                      </div>
                    </div>
                  )}
                  {uploadResult.errors?.length > 0 && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <p className="font-bold text-red-700">{uploadResult.errors.length} row(s) had errors</p>
                      </div>
                      <ul className="text-xs text-slate-700 space-y-1 max-h-64 overflow-y-auto">
                        {uploadResult.errors.map((e, i) => (
                          <li key={i} className="bg-white rounded px-2 py-1 border border-red-100">
                            <strong>Row {e.row}:</strong> {e.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              )}
            </Card>
          </motion.div>
        )}

        {/* DRAFTS TAB — MCQ Agent output pending admin review */}
        {tab === 'drafts' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <MCQAgentDrafts />
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default QuestionManagerPage;
